/**
 * Error banner. Maps the discriminated AnswerError to Spanish (or English)
 * copy per Task 057 §"Error shapes". Restyled for the walnut/gold design;
 * logic preserved.
 */
import type { AnswerError } from "../api/answer-client";
import type { Strings } from "../strings";

export interface ErrorBannerProps {
  strings: Strings;
  error: AnswerError;
  onRetry: () => void;
  onDismiss: () => void;
}

function formatError(strings: Strings, error: AnswerError): string {
  switch (error.kind) {
    case "rate_limit":
      return strings.errorRateLimit(
        typeof error.retryAfterSeconds === "number" ? error.retryAfterSeconds : 60,
      );
    case "query_too_long":
      return strings.errorQueryTooLong(
        typeof error.limit === "number" ? error.limit : 1000,
      );
    case "too_many_sources":
      return strings.errorTooManySources;
    case "network":
      return strings.errorNetwork;
    case "service_unavailable":
      return strings.errorServiceUnavailable;
    case "unknown":
      return strings.errorUnknown;
    default: {
      const exhaustive: never = error.kind;
      void exhaustive;
      return strings.errorUnknown;
    }
  }
}

export function ErrorBanner(props: ErrorBannerProps): JSX.Element {
  const { strings, error, onRetry, onDismiss } = props;
  const message = formatError(strings, error);
  const canRetry = error.kind !== "query_too_long";

  return (
    <div className="error-banner" role="alert">
      <p className="error-banner__message">{message}</p>
      <div className="error-banner__actions">
        {canRetry ? (
          <button
            type="button"
            className="error-banner__retry"
            onClick={onRetry}
          >
            {strings.errorRetry}
          </button>
        ) : null}
        <button
          type="button"
          className="error-banner__dismiss"
          onClick={onDismiss}
        >
          {strings.errorDismiss}
        </button>
      </div>
    </div>
  );
}
